import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Phone, Mail, Truck, User, IdCard, ChevronDown, ChevronUp, Download } from 'lucide-react';

const EMPTY_FORM = {
  name: '', phone: '', email: '', carrier_id: '',
  truck_number: '', trailer_number: '', truck_type: 'Semi Truck',
  home_base_zip: '', home_base_city: '', home_base_state: '',
  assigned_agent_id: '',
  license_number: '', license_state: '', license_expiration: '', cdl_class: '', medical_card_expiration: '',
  dob: '', address: '', city: '', state: '', zip_code: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '',
  ceo_name: '', ceo_phone: '', ceo_email: '',
  compliance_notes: '', notes: '',
};

const CDL_CLASSES = ['A', 'B', 'C'];

export default function Drivers({ user }) {
  const [drivers, setDrivers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [users, setUsers] = useState([]);

  const isSuperAdmin = user?.role === 'super_admin';
  const isP3Manager = user?.role === 'broker_manager' && user?.ai_mode === 'autonomous';
  const canAssign = user?.role === 'super_admin' || user?.role === 'broker' || isP3Manager;

  useEffect(() => { loadData(); }, []);

  const loadData = () => {
    setLoading(true);
    const requests = [api.get('/drivers'), api.get('/carriers')];
    if (canAssign) requests.push(api.get('/agents').catch(() => ({ data: [] })));
    Promise.all(requests)
      .then(([dRes, cRes, uRes]) => {
        setDrivers(dRes.data);
        setCarriers(cRes.data);
        if (uRes) setUsers(uRes.data || []);
      })
      .catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  };

  // Phase 3 dispatchers (users who can be assigned drivers)
  const dispatchers = users.filter(u => u.ai_mode === 'autonomous' && (u.role === 'broker_manager' || u.role === 'broker'));

  const reassignDispatcher = async (driver, newDispatcherId) => {
    // newDispatcherId '' means unassign
    try {
      if (newDispatcherId) {
        // Enforce cap of 5 via manager endpoint (atomic — replaces roster)
        const rosterRes = await api.get(`/users/${newDispatcherId}/assigned-drivers`);
        const currentIds = (rosterRes.data.drivers || []).map(x => x.id).filter(id => id !== driver.id);
        if (currentIds.length >= 5) {
          toast.error(`That dispatcher already has 5 drivers (max). Free a slot first.`);
          return;
        }
        await api.post(`/users/${newDispatcherId}/assign-drivers`, {
          driver_ids: [...currentIds, driver.id],
        });
        const name = dispatchers.find(x => x.id === newDispatcherId)?.name || 'dispatcher';
        toast.success(`${driver.name} → ${name}`);
      } else {
        // Unassign — use direct driver update (fields are already writable via PUT /drivers)
        await api.put(`/drivers/${driver.id}`, {
          ...driver,
          assigned_agent_id: null,
          assigned_agent_name: null,
        });
        toast.success(`${driver.name} unassigned`);
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reassign');
    }
  };

  const carrierName = (id) => carriers.find(c => c.id === id)?.legal_name || 'Unknown';

  const agentLabel = (d) => {
    if (!isSuperAdmin) return null;
    if (d.created_by_pin) {
      const agent = users.find(u => u.pin_code === d.created_by_pin);
      return agent ? `${agent.name} (PIN ${d.created_by_pin})` : `Agent PIN: ${d.created_by_pin}`;
    }
    if (d.created_by) {
      const creator = users.find(u => u.id === d.created_by);
      return creator ? `${creator.name}` : 'Master Admin';
    }
    return null;
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      name: d.name || '', phone: d.phone || '', email: d.email || '', carrier_id: d.carrier_id || '',
      truck_number: d.truck_number || '', trailer_number: d.trailer_number || '',
      truck_type: d.truck_type || 'Semi Truck',
      home_base_zip: d.home_base_zip || '', home_base_city: d.home_base_city || '', home_base_state: d.home_base_state || '',
      assigned_agent_id: d.assigned_agent_id || '',
      license_number: d.license_number || '', license_state: d.license_state || '',
      license_expiration: d.license_expiration || '', cdl_class: d.cdl_class || '', medical_card_expiration: d.medical_card_expiration || '',
      dob: d.dob || '', address: d.address || '', city: d.city || '', state: d.state || '', zip_code: d.zip_code || '',
      emergency_contact_name: d.emergency_contact_name || '', emergency_contact_phone: d.emergency_contact_phone || '',
      emergency_contact_relation: d.emergency_contact_relation || '',
      ceo_name: d.ceo_name || '', ceo_phone: d.ceo_phone || '', ceo_email: d.ceo_email || '',
      compliance_notes: d.compliance_notes || '',
      notes: d.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/drivers/${editingId}`, form);
        toast.success('Driver updated');
      } else {
        await api.post('/drivers', form);
        toast.success('Driver added');
      }
      setShowForm(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save driver');
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete driver "${d.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/drivers/${d.id}`);
      toast.success('Driver deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const exportToExcel = () => {
    api.get('/export/drivers', { responseType: 'blob' })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'drivers.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Drivers exported');
      })
      .catch(() => toast.error('Failed to export'));
  };

  const filtered = drivers.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.name?.toLowerCase().includes(q) || d.phone?.includes(q) || d.email?.toLowerCase().includes(q) || d.license_number?.toLowerCase().includes(q);
  });

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="drivers-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Drivers</h1>
          <p className="text-slate-600 mt-1">{drivers.length} driver{drivers.length !== 1 ? 's' : ''} registered</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToExcel} data-testid="export-drivers-btn">
            <Download className="w-4 h-4 mr-2" /> Export XLSX
          </Button>
          <Button onClick={openCreate} data-testid="add-driver-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Driver
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input className="pl-9" placeholder="Search by name, phone, email, license..." value={search} onChange={e => setSearch(e.target.value)} data-testid="driver-search" />
      </div>

      {/* Driver List */}
      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">{drivers.length === 0 ? 'No drivers yet. Add your first driver.' : 'No drivers match your search.'}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(d => {
            const expanded = expandedId === d.id;
            const isExpired = d.license_expiration && new Date(d.license_expiration) < new Date();
            return (
              <Card key={d.id} className={`transition-all ${d.status === 'inactive' ? 'opacity-60' : ''}`} data-testid={`driver-card-${d.id}`}>
                <CardContent className="pt-4 pb-3">
                  {/* Summary Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(expanded ? null : d.id)}>
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 truncate">{d.name}</h3>
                          {d.cdl_class && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">CDL-{d.cdl_class}</span>}
                          {isExpired && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">License Expired</span>}
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${d.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{d.status || 'active'}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</span>
                          {d.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{d.email}</span>}
                          <span className="flex items-center gap-1"><Truck className="w-3 h-3" />{carrierName(d.carrier_id)}</span>
                          {d.truck_number && <span>Truck #{d.truck_number}</span>}
                          {d.assigned_agent_name ? (
                            <span
                              className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium"
                              data-testid={`dispatcher-chip-${d.id}`}
                            >
                              ▸ {d.assigned_agent_name}
                            </span>
                          ) : (
                            <span
                              className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 italic"
                              data-testid={`dispatcher-chip-${d.id}`}
                            >
                              Unassigned
                            </span>
                          )}
                          {agentLabel(d) && <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-medium" data-testid={`agent-label-${d.id}`}>{agentLabel(d)}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {canAssign && (
                        <div className="hidden md:flex flex-col items-end gap-0.5" data-testid={`dispatcher-cell-${d.id}`}>
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">Dispatcher</span>
                          <select
                            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 max-w-[180px]"
                            value={d.assigned_agent_id || ''}
                            onChange={(e) => reassignDispatcher(d, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`assign-dispatcher-${d.id}`}
                          >
                            <option value="">— Unassigned —</option>
                            {dispatchers.map(m => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-driver-${d.id}`}><Edit className="w-4 h-4" /></Button>
                      {user.role !== 'agent' && (
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(d)} data-testid={`delete-driver-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setExpandedId(expanded ? null : d.id)}>
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expanded && (
                    <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-testid={`driver-details-${d.id}`}>
                      <div><p className="text-slate-500 text-xs">License #</p><p className="font-medium">{d.license_number || '—'}</p></div>
                      <div><p className="text-slate-500 text-xs">License State</p><p className="font-medium">{d.license_state || '—'}</p></div>
                      <div><p className="text-slate-500 text-xs">License Exp.</p><p className={`font-medium ${isExpired ? 'text-red-600' : ''}`}>{d.license_expiration || '—'}</p></div>
                      <div><p className="text-slate-500 text-xs">CDL Class</p><p className="font-medium">{d.cdl_class || '—'}</p></div>
                      <div><p className="text-slate-500 text-xs">Medical Card Exp.</p><p className={`font-medium ${d.medical_card_expiration && new Date(d.medical_card_expiration) < new Date() ? 'text-red-600' : ''}`}>{d.medical_card_expiration || '—'}</p></div>
                      <div><p className="text-slate-500 text-xs">DOB</p><p className="font-medium">{d.dob || '—'}</p></div>
                      <div><p className="text-slate-500 text-xs">Truck #</p><p className="font-medium">{d.truck_number || '—'}</p></div>
                      <div><p className="text-slate-500 text-xs">Trailer #</p><p className="font-medium">{d.trailer_number || '—'}</p></div>
                      <div><p className="text-slate-500 text-xs">Carrier</p><p className="font-medium">{carrierName(d.carrier_id)}</p></div>
                      {(d.address || d.city) && (
                        <div className="col-span-2"><p className="text-slate-500 text-xs">Address</p><p className="font-medium">{[d.address, d.city, d.state, d.zip_code].filter(Boolean).join(', ') || '—'}</p></div>
                      )}
                      {d.emergency_contact_name && (
                        <div className="col-span-2"><p className="text-slate-500 text-xs">Emergency Contact</p><p className="font-medium">{d.emergency_contact_name} — {d.emergency_contact_phone || 'N/A'}{d.emergency_contact_relation ? ` (${d.emergency_contact_relation})` : ''}</p></div>
                      )}
                      {d.ceo_name && (
                        <div className="col-span-2"><p className="text-slate-500 text-xs">CEO / Owner</p><p className="font-medium">{d.ceo_name} — {d.ceo_phone || 'N/A'}{d.ceo_email ? ` | ${d.ceo_email}` : ''}</p></div>
                      )}
                      {d.compliance_notes && (
                        <div className="col-span-4"><p className="text-slate-500 text-xs">Compliance Notes</p><p className="font-medium">{d.compliance_notes}</p></div>
                      )}
                      {d.folder_number && (
                        <div><p className="text-slate-500 text-xs">File #</p><p className="font-medium text-blue-600">{d.folder_number}</p></div>
                      )}
                      {d.notes && (
                        <div className="col-span-4"><p className="text-slate-500 text-xs">Notes</p><p className="font-medium">{d.notes}</p></div>
                      )}
                      {isSuperAdmin && agentLabel(d) && (
                        <div className="col-span-2"><p className="text-slate-500 text-xs">Assigned To</p><p className="font-medium text-purple-700">{agentLabel(d)}</p></div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Driver Profile' : 'Add New Driver'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Basic Info */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><User className="w-4 h-4" /> Basic Information</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full Name *</Label><Input required value={form.name} onChange={set('name')} data-testid="form-driver-name" /></div>
                <div><Label>Phone *</Label><Input required type="tel" value={form.phone} onChange={set('phone')} data-testid="form-driver-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={set('email')} data-testid="form-driver-email" /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={form.dob} onChange={set('dob')} data-testid="form-driver-dob" /></div>
              </div>
            </div>

            {/* License / CDL */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><IdCard className="w-4 h-4" /> License &amp; CDL</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>License Number</Label><Input value={form.license_number} onChange={set('license_number')} data-testid="form-license-number" /></div>
                <div><Label>License State</Label><Input value={form.license_state} onChange={set('license_state')} placeholder="e.g. TX" data-testid="form-license-state" /></div>
                <div><Label>License Expiration</Label><Input type="date" value={form.license_expiration} onChange={set('license_expiration')} data-testid="form-license-exp" /></div>
                <div><Label>Medical Card Expiration</Label><Input type="date" value={form.medical_card_expiration} onChange={set('medical_card_expiration')} data-testid="form-medical-card-exp" /></div>
                <div>
                  <Label>CDL Class</Label>
                  <Select value={form.cdl_class} onValueChange={v => setForm(prev => ({ ...prev, cdl_class: v }))}>
                    <SelectTrigger data-testid="form-cdl-class"><SelectValue placeholder="Select CDL class" /></SelectTrigger>
                    <SelectContent>
                      {CDL_CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Carrier / Equipment */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Truck className="w-4 h-4" /> Carrier &amp; Equipment</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Carrier *</Label>
                  <Select value={form.carrier_id} onValueChange={v => setForm(prev => ({ ...prev, carrier_id: v }))} required>
                    <SelectTrigger data-testid="form-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                    <SelectContent>
                      {carriers.map(c => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Truck Number</Label><Input value={form.truck_number} onChange={set('truck_number')} data-testid="form-truck" /></div>
                <div><Label>Trailer Number</Label><Input value={form.trailer_number} onChange={set('trailer_number')} data-testid="form-trailer" /></div>
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                <div><Label>Truck Type <span className="text-amber-600 text-xs">(Fleet Rates)</span></Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.truck_type} onChange={set('truck_type')} data-testid="form-truck-type">
                    <option>Semi Truck</option><option>Box Truck</option><option>Cargo Van</option><option>Sprinter Van</option>
                  </select>
                </div>
                <div><Label>Home Base Zip <span className="text-amber-600 text-xs">(Phase 3)</span></Label><Input placeholder="75001" value={form.home_base_zip} onChange={set('home_base_zip')} data-testid="form-home-zip" /></div>
                <div><Label>Home City</Label><Input placeholder="Dallas" value={form.home_base_city} onChange={set('home_base_city')} data-testid="form-home-city" /></div>
                <div><Label>Home State</Label><Input placeholder="TX" value={form.home_base_state} onChange={set('home_base_state')} data-testid="form-home-state" /></div>
              </div>
              {canAssign && (
                <div className="mt-3">
                  <Label>Assigned Dispatcher <span className="text-blue-600 text-xs">(broker-only)</span></Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.assigned_agent_id} onChange={set('assigned_agent_id')} data-testid="form-assigned-agent">
                    <option value="">— Unassigned —</option>
                    {users.filter(u => u.role === 'agent' || u.role === 'broker_manager').map(u => <option key={u.id} value={u.id}>{u.name} — {u.role.replace('_',' ')}</option>)}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">Assigned dispatcher gets notified when driver uploads BOL/POD or updates status.</p>
                </div>
              )}
            </div>

            {/* Address */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Address</p>
              <div className="grid grid-cols-1 gap-3">
                <div><Label>Street Address</Label><Input value={form.address} onChange={set('address')} data-testid="form-address" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>City</Label><Input value={form.city} onChange={set('city')} /></div>
                  <div><Label>State</Label><Input value={form.state} onChange={set('state')} /></div>
                  <div><Label>Zip</Label><Input value={form.zip_code} onChange={set('zip_code')} /></div>
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">Emergency Contact</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={set('emergency_contact_name')} data-testid="form-emergency-name" /></div>
                <div><Label>Phone</Label><Input type="tel" value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} data-testid="form-emergency-phone" /></div>
                <div><Label>Relationship</Label><Input placeholder="Spouse, Parent, etc." value={form.emergency_contact_relation} onChange={set('emergency_contact_relation')} data-testid="form-emergency-relation" /></div>
              </div>
            </div>

            {/* CEO / Owner Info */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">CEO / Owner (if applicable)</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>CEO Name</Label><Input value={form.ceo_name} onChange={set('ceo_name')} data-testid="form-ceo-name" /></div>
                <div><Label>CEO Phone</Label><Input type="tel" value={form.ceo_phone} onChange={set('ceo_phone')} data-testid="form-ceo-phone" /></div>
                <div><Label>CEO Email</Label><Input type="email" value={form.ceo_email} onChange={set('ceo_email')} data-testid="form-ceo-email" /></div>
              </div>
            </div>

            {/* Compliance & Notes */}
            <div>
              <Label>Compliance Notes</Label>
              <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Insurance details, special requirements, etc." value={form.compliance_notes} onChange={set('compliance_notes')} data-testid="form-compliance-notes" />
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.notes} onChange={set('notes')} data-testid="form-notes" />
            </div>

            <Button type="submit" className="w-full" data-testid="save-driver-btn">
              {editingId ? 'Update Driver' : 'Add Driver'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
